import React, { useCallback, useEffect, useState } from "react";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import styles from "./Referrals.module.sass";

// ── Formato (con el idioma del backoffice) ─────────────────────────────────
export const useFormat = () => {
  const { i18n } = useTranslation();
  const locale = i18n.language || "es";
  return {
    locale,
    num: (v) => (v == null ? "—" : Number(v).toLocaleString(locale)),
    /** Céntimos de EUR → "12,34 €". */
    eur: (cents) =>
      cents == null ? "—" : (Number(cents) / 100).toLocaleString(locale, { style: "currency", currency: "EUR" }),
    date: (d) => (d ? new Date(d).toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" }) : "—"),
    dateTime: (d) =>
      d
        ? new Date(d).toLocaleString(locale, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
        : "—",
  };
};

// ── Respuestas de la API (general.service devuelve el error, no lo lanza) ──
/** Nombres de marca: no se traducen. */
export const PLATFORM_LABEL = { ios: "iOS", android: "Android" };

export const isOk = (resp) => !!resp && resp.status >= 200 && resp.status < 300;
export const errorCodeOf = (resp) =>
  resp?.response?.data?.error || (resp?.response?.status ? `http_${resp.response.status}` : "network");
export const errorDetailsOf = (resp) => resp?.response?.data?.details || [];

/** Mensaje traducido de un código de error de la API (04 §6). */
export const useErrorText = () => {
  const { t } = useTranslation();
  return (code) => t(`referrals.errors.${code}`, { defaultValue: t("referrals.errors.generic") });
};

// ── Estados con color ───────────────────────────────────────────────────────
const TONES = {
  ATTRIBUTED: "blue",
  REVIEW: "orange",
  CONVERTED: "green",
  REJECTED: "red",
  ACTIVE: "green",
  PAUSED: "orange",
  REVOKED: "red",
  DRAFT: "orange",
  RETIRED: "grey",
  PENDING: "orange",
  PROCESSING: "blue",
  APPLIED: "green",
  BANKED: "blue",
  NOT_APPLICABLE: "grey",
  CAPPED: "grey",
  CANCELLED: "red",
  FAILED: "red",
};

export const StatusBadge = ({ status }) => {
  const { t } = useTranslation();
  if (!status) return null;
  return (
    <span className={cn(styles.badge, styles[`tone_${TONES[status] || "grey"}`])}>
      {t(`referrals.status.${status}`, { defaultValue: status })}
    </span>
  );
};

export const JsonBlock = ({ value }) => <pre className={styles.json}>{JSON.stringify(value, null, 2)}</pre>;

/**
 * Listado paginado por cursor (04: `{ items, nextCursor }`, nunca skip). Guarda
 * la pila de cursores para poder volver atrás.
 */
export const useCursorList = (fetcher, params) => {
  const [stack, setStack] = useState([null]);
  const [page, setPage] = useState({ items: [], nextCursor: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const key = JSON.stringify(params);
  const cursor = stack[stack.length - 1];

  const load = useCallback(async () => {
    setLoading(true);
    const resp = await fetcher({ ...params, cursor: cursor || undefined });
    setLoading(false);
    if (isOk(resp)) {
      setPage(resp.data);
      setError(null);
    } else setError(errorCodeOf(resp));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, cursor]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => setStack([null]), [key]);

  return {
    ...page,
    loading,
    error,
    reload: load,
    hasPrev: stack.length > 1,
    next: () => page.nextCursor && setStack((s) => [...s, page.nextCursor]),
    prev: () => setStack((s) => (s.length > 1 ? s.slice(0, -1) : s)),
  };
};

export const CursorPager = ({ list }) => {
  const { t } = useTranslation();
  if (!list.hasPrev && !list.nextCursor) return null;
  return (
    <div className={styles.pager}>
      <button type="button" className="button-stroke button-small" disabled={!list.hasPrev} onClick={list.prev}>
        ‹ {t("referrals.common.prev")}
      </button>
      <button type="button" className="button-stroke button-small" disabled={!list.nextCursor} onClick={list.next}>
        {t("referrals.common.next")} ›
      </button>
    </div>
  );
};

/** Pide un texto (motivo, nota) con window.prompt: suficiente para una herramienta interna. */
export const askReason = (t, key = "referrals.common.reasonPrompt") => {
  const value = window.prompt(t(key));
  return value && value.trim() ? value.trim() : null;
};
